/**
 * The words of a `text` stroke (`KLQ-20260820-001` phase 5, issue #71).
 *
 * `MarkupLayer` takes a text stroke's *place* from a press and its *value* from whoever owns
 * the tool — this is that owner's field. It floats at the top of the stage rather than beside
 * the point: the draft is already drawn on the sheet as it is typed (`MarkupLayer`'s `draft`),
 * so the preview is where the text will be, and a box anchored over that point would cover the
 * very words it is previewing.
 *
 * **Nothing has been written while this is open**, which is why Cancel is honest here and why
 * it is the rail's only form of undo. Place sends one `POST /markups`; after that the stroke is
 * the version's and no control on this screen takes it back.
 */

import { useEffect, useRef } from 'react';

import { Button, InlineError } from '@/components';
import { apiErrorKey } from '@/features/drawings';
import { useMessages } from '@/lib/i18n';

import styles from './Viewer.module.css';

export interface MarkupTextComposerProps {
  readonly value: string;
  readonly sending: boolean;
  /** The last attempt's failure. The words survive it. */
  readonly error: unknown;
  readonly onChange: (value: string) => void;
  readonly onPlace: () => void;
  readonly onCancel: () => void;
}

export function MarkupTextComposer({
  value,
  sending,
  error,
  onChange,
  onPlace,
  onCancel,
}: MarkupTextComposerProps) {
  const t = useMessages('drawings');
  const field = useRef<HTMLInputElement | null>(null);

  // The press chose the place; the keyboard should already be in the box — `PinComposer`'s
  // reasoning, same gesture.
  useEffect(() => {
    field.current?.focus();
  }, []);

  const empty = value.trim() === '';

  return (
    <form
      className={styles.textComposer}
      onSubmit={(event) => {
        event.preventDefault();
        if (!empty && !sending) onPlace();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
    >
      <input
        ref={field}
        className={styles.pinField}
        value={value}
        maxLength={500}
        placeholder={t('markup.text.placeholder')}
        aria-label={t('markup.text.placeholder')}
        onChange={(event) => {
          onChange(event.currentTarget.value);
        }}
      />
      {error == null ? null : <InlineError variant="inline">{t(apiErrorKey(error))}</InlineError>}
      <span className={styles.pinActions}>
        <Button variant="tertiary" size="sm" onClick={onCancel}>
          {t('pin.cancel')}
        </Button>
        <Button variant="primary" size="sm" type="submit" disabled={empty} loading={sending}>
          {t('markup.text.place')}
        </Button>
      </span>
    </form>
  );
}
